import React, { useEffect, useRef, useState } from 'react';
import { Check, Copy, Info, Trash2 } from 'lucide-react';
import { isoDate, fmtHM, parseDuration } from '../lib/time.js';
import { isWebUrl } from '../lib/url.js';
import {
  FUTURE_WORK_MESSAGE,
  isFutureWork,
  isFutureWorkNow,
  orgToday,
  projectsForDate,
} from '../lib/futureWork.js';
import { Modal, TimeField } from './ui.jsx';
import { ProjectPicker } from './ProjectPicker.jsx';
import { DatePicker } from './controls/DatePicker.jsx';

// Duration is a span of time, not a clock value, so it is rendered as a plain
// "H:MM" text field (via fmtHM) instead of a <input type="time"> (which forces
// AM/PM in 12-hour locales).

export function EntryModal({ initial, data, onSave, onDelete, onClose }) {
  const today = isoDate(new Date());
  const [projectId, setProjectId] = useState(initial.projectId || '');
  const [workDate, setWorkDate] = useState(initial.workDate || today);
  const [start, setStart] = useState(initial.start ?? 9);
  const [end, setEnd] = useState(initial.end ?? 10);
  const [description, setDescription] = useState(initial.description || '');
  // null | { field: 'project' | 'time' | 'save', message: string }
  const [error, setError] = useState(null);
  const [durationText, setDurationText] = useState(() => fmtHM(end - start));
  const [saving, setSaving] = useState(false);
  const durationFocused = useRef(false);
  const descriptionRef = useRef(null);
  const submitRef = useRef(null);
  const formRef = useRef(null);
  const isEdit = Boolean(initial.id);
  const selectedProject = data.projects.find((p) => p.id === projectId);
  // A future day takes absence only: the picker offers just those, and work
  // on a future day says why it can't be saved (the date picker stays open,
  // as a bound would only report a "bad date"). An entry already in the
  // future on work (logged before the rule) is shown as it is, and only
  // saves once it is moved to today or earlier. The picked project always
  // stays listed, so the picker never goes blank under the error. "Future"
  // is after the org's today, as the server decides it.
  const orgDay = orgToday(data.orgTimezone);
  const pickerProjects = projectsForDate(
    data.projects,
    workDate,
    orgDay,
    projectId,
  );
  const futureWork = isFutureWork(selectedProject, workDate, orgDay);
  const futureDay = workDate > orgDay;

  // Validate before saving/duplicating: a project is required and the entry
  // must end after it starts. Also guards against a second save firing while
  // one is in flight — the disabled buttons only block mouse clicks, not the
  // Enter-key handler or form submit.
  async function trySave(payload) {
    if (saving) return;
    // Duplicate and the Enter shortcut skip the native submit check, which
    // is what blocks a date the picker could not read.
    if (formRef.current && !formRef.current.reportValidity()) return;
    if (!projectId) {
      setError({ field: 'project', message: 'Please select a project.' });
      return;
    }
    if (end <= start) {
      setError({
        field: 'time',
        message: 'The "To" time must be after the "From" time.',
      });
      return;
    }
    if (isFutureWorkNow(selectedProject, workDate, data.orgTimezone)) return;
    setError(null);
    setSaving(true);
    try {
      await onSave(payload);
    } catch (err) {
      setError({
        field: 'save',
        message: err.message || 'Could not save. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  }

  // Latest save closure, so the (once-registered) Enter handler stays current.
  submitRef.current = () =>
    trySave({ ...initial, projectId, workDate, start, end, description });

  useEffect(() => {
    const input = descriptionRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, []);

  // Enter blurs a focused field; with no field focused it saves. (A focused
  // button or textarea keeps its own Enter behaviour.)
  useEffect(() => {
    function onKeyDown(event) {
      if (event.key !== 'Enter') return;
      const el = document.activeElement;
      const tag = el && el.tagName;
      if (tag === 'INPUT' || tag === 'SELECT') {
        event.preventDefault();
        el.blur();
      } else if (tag !== 'TEXTAREA' && tag !== 'BUTTON') {
        event.preventDefault();
        submitRef.current();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // Keep the duration text in sync when From/To change it, but never fight the
  // user while they are typing into the duration field itself.
  useEffect(() => {
    if (!durationFocused.current) setDurationText(fmtHM(end - start));
    setError((prev) => (prev?.field === 'time' ? null : prev));
  }, [start, end]);

  return (
    <Modal title={isEdit ? 'Edit time entry' : 'Log time'} onClose={onClose}>
      <form
        ref={formRef}
        className="form-grid"
        onSubmit={(event) => {
          event.preventDefault();
          trySave({ ...initial, projectId, workDate, start, end, description });
        }}
      >
        <div className={error?.field === 'project' ? 'field-invalid' : ''}>
          <span className="modal-label">Project</span>
          <ProjectPicker
            projects={pickerProjects}
            entries={data.entries}
            value={projectId}
            onChange={(value) => {
              setProjectId(value);
              setError(null);
            }}
            mode="picker"
            recentStrategy="most-used"
          />
          {error?.field === 'project' && (
            <p className="field-error">{error.message}</p>
          )}
        </div>
        <ProjectGuidance project={selectedProject} />
        <label>
          Description
          <input
            ref={descriptionRef}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Optional"
          />
        </label>
        <div className="three">
          <label>
            Duration
            <input
              type="text"
              inputMode="numeric"
              placeholder="H:MM"
              value={durationText}
              onFocus={(event) => {
                durationFocused.current = true;
                // Select the whole value so compact digit entry ("1030",
                // "0015") types over the pre-filled "H:MM" instead of being
                // inserted into it — matching the From/To fields.
                const input = event.currentTarget;
                window.setTimeout(() => input.select(), 0);
              }}
              onChange={(event) => {
                setDurationText(event.target.value);
                const dur = parseDuration(event.target.value);
                if (dur != null) setEnd(Math.min(24, start + dur));
              }}
              onBlur={() => {
                durationFocused.current = false;
                setDurationText(fmtHM(end - start));
              }}
            />
          </label>
          <label>
            From
            <TimeField value={start} onChange={setStart} />
          </label>
          <label>
            To
            <TimeField value={end} onChange={setEnd} />
          </label>
        </div>
        {error?.field === 'time' && (
          <p className="field-error">{error.message}</p>
        )}
        <label className={futureWork ? 'field-invalid' : ''}>
          Date
          <DatePicker
            ariaLabel="Date"
            value={workDate}
            onChange={setWorkDate}
            required
          />
        </label>
        {futureWork ? (
          <p className="field-error">{FUTURE_WORK_MESSAGE}</p>
        ) : (
          futureDay && (
            <p className="hint">
              A future day takes absence only: vacation, sick leave, doctor,
              public holiday…
            </p>
          )
        )}
        {error?.field === 'save' && (
          <p className="field-error">{error.message}</p>
        )}
        <div className="modal-actions">
          {isEdit && (
            <button
              type="button"
              className="danger"
              disabled={saving}
              onClick={() => onDelete(initial.id)}
            >
              <Trash2 size={14} /> Delete
            </button>
          )}
          <span className="spacer" />
          {isEdit && (
            <button
              type="button"
              className="secondary"
              disabled={saving}
              onClick={() =>
                trySave({ projectId, workDate, start, end, description })
              }
            >
              <Copy size={14} /> Duplicate
            </button>
          )}
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="primary" disabled={saving}>
            <Check size={14} /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// How-to-log guidance an admin attached to the project (e.g. the HPP absence
// categories) — shown as soon as the project is picked, before saving.
function ProjectGuidance({ project }) {
  const text = project?.loggingHint?.trim();
  if (!text) return null;
  return (
    <div className="project-guidance" role="note">
      <span className="project-guidance-icon">
        <Info size={16} />
      </span>
      <div>
        <strong>How to log {project.name}</strong>
        <p>{text}</p>
        {isWebUrl(project.loggingHintUrl) && (
          <a
            href={project.loggingHintUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Full guide
          </a>
        )}
      </div>
    </div>
  );
}
